import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, aboutContent } from '@/mocks/movies';

export default function AboutPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '555影视是否需要注册才能观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。555影视所有电影资源信息完全免费，无需注册即可浏览和查找您想看的电影资源。',
        },
      },
      {
        '@type': 'Question',
        name: '555影视的电影资源是否高清？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的。555影视致力于提供高清电影资源信息，我们筛选的片源均为高画质资源，播放流畅不缓冲。',
        },
      },
      {
        '@type': 'Question',
        name: '555影视更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '555影视每日更新最新电影资源信息，确保您能第一时间获取院线热映和网络新片的信息。',
        },
      },
      {
        '@type': 'Question',
        name: '555影视可以在手机上观看吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '可以。555影视网站采用响应式设计，完美适配手机、平板和电脑等各类设备，让您随时随地畅享观影。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系555影视？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过以下方式联系我们：邮箱 ${siteInfo.email}，电话 ${siteInfo.phone}，或关注我们的微信公众号 ${siteInfo.wechat}。`,
        },
      },
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: `${siteInfo.name}成立于${siteInfo.founded}年，${aboutContent.intro}`,
    url: 'https://555mov.com/about',
  };

  return (
    <>
      <PageSEO
        title={`关于我们 - ${siteInfo.fullName}`}
        description={`${aboutContent.intro} ${siteInfo.name}是国内领先的在线电影资源导航平台，致力于为广大影迷提供最新、最全、最高清的电影资源信息。`}
        keywords={`关于555影视,555影视简介,${siteInfo.fullName},影视平台,在线电影,免费电影`}
        canonicalUrl="https://555mov.com/about"
        schema={{ ...aboutPageSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于<span className="text-primary-500">555影视</span>
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                {aboutContent.intro}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
              {aboutContent.stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-6 text-center border border-background-200"
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Mission */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-center gap-10">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    我们的使命
                  </h2>
                  <p className="text-foreground-600 leading-relaxed text-sm mb-6">
                    {aboutContent.mission}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className="ri-heart-line text-xl text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">用心服务</div>
                      <div className="text-xs text-foreground-500">让每一位影迷满意</div>
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20a%20large%20projection%20screen%20showing%20movie%20posters%2C%20warm%20ambient%20lighting%2C%20creative%20workspace%2C%20professional%20atmosphere%2C%20clean%20aesthetic%2C%20cinematic%20vibe&width=800&height=500&seq=about-hero-555&orientation=landscape"
                      alt="555影视团队"
                      className="w-full h-64 md:h-80 object-cover object-top"
                      title="555影视团队 - 555影视"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Honors */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                行业荣誉
              </h2>
              <p className="text-foreground-600 text-sm">多年来获得的行业认可与肯定</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {aboutContent.honors.map((honor, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 bg-white rounded-xl p-5 border border-background-200 hover:-translate-y-1 transition-transform duration-300"
                >
                  <div className="w-12 h-12 flex-shrink-0 rounded-full bg-accent-50 flex items-center justify-center">
                    <i className="ri-award-line text-xl text-accent-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950 mb-1">
                      {honor.title}
                    </div>
                    <div className="text-xs text-foreground-500">{honor.year}年</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm">如有任何问题或建议，欢迎随时与我们联系</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
                  { icon: 'ri-phone-line', label: '客服电话', value: siteInfo.phone },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: siteInfo.wechat },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
                ].map((item, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-5 text-center border border-background-200">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className={`${item.icon} text-xl text-primary-500`}></i>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                    <div className="text-sm font-medium text-foreground-950">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm">关于555影视的常见问题解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqSchema.mainEntity.map((item, idx) => {
                const question = 'name' in item ? item.name : '';
                const answer = 'acceptedAnswer' in item && item.acceptedAnswer && 'text' in item.acceptedAnswer ? item.acceptedAnswer.text : '';
                return (
                  <details key={idx} className="group bg-white rounded-xl border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                      <span className="text-sm font-medium text-foreground-950">{question}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden text-sm"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-sm"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
                    </div>
                  </details>
                );
              })}
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}